<template>
  <div>
    <SlidingPanel :isPanelOpen="isPanelOpen" @closePanel="isPanelOpen = false">
      <template #content>
        <AddTalentPanel  @close="isPanelOpen = false"></AddTalentPanel>
      </template>
    </SlidingPanel>
    <div class="button-header">
      <Button class="icon icon-left primary light-text sm" :label="$t('app.campaign.follow.headerButtons.addTalent')" :disabled="campaign.archived" @click="goToNewTalent">
        <template #prefix>
          <PlusIcon style="width: 16px;"></PlusIcon>
        </template>
      </Button>
    </div>
    <div class="cards-container">
      <SimpleStatCard class="card-item" :label="$t('app.campaign.summary.participantCountStat.label')" :stat="isParticipationsPending ? 0 : (participationStats?.numberOfParticipants || 0)">
        <template #icon>
          <TalentNumberIcon style="width: 18px;">
          </TalentNumberIcon>
        </template>
      </SimpleStatCard>
      <SimpleStatCard class="card-item" :label="$t('app.campaign.summary.compabilityAverageStat.label')" :stat="isParticipationsPending ? 0 : (Math.round((participationStats?.averageCompatibilityScore || 0) * 100))">
        <template #icon>
          <CompatibilityScoreIcon style="width: 18px;">
          </CompatibilityScoreIcon>
        </template>
      </SimpleStatCard>
    </div>
    <div class="best-and-last-talents-block">
      <div class="best-talents">
        <p class="card-label">{{ $t('app.campaign.summary.bestTalents.label') }}</p>
        <div v-if="bestParticipants?.length" class="last-talents-cards">
          <div v-for="(participant, index) in bestParticipants" :key="index" class="card talent-card" @click="goToCandidateResults(participant)">
            <ProgressCircle :isNone="participant?.results?.compatibility === undefined" :size="40" :stroke="5" :gapAngle="90" :percentage="participant?.results?.compatibility * 100" />
            <div class="no-grow">
              <p class="candidate-name">
                {{ participant?.candidate?.first_name || participant?.pending_first_name || participant?.pending_email }} {{ participant?.candidate?.last_name || participant?.pending_last_name || '' }}
              </p>
              <p class="pass-date">
                {{ $t('app.campaign.summary.lastAvailableResults.assessmentPassedLabel') }} {{ useDateFormat(participant.results.completed_at, 'DD/MM/YYYY') }}
              </p>
            </div>
          </div>
        </div>
        <Empty v-else :message="$t('app.home.noResults')" :dark="isDark" icon="folder" />
      </div>
      <div class="last-talents">
        <p class="card-label">{{ $t('app.campaign.summary.lastAvailableResults.label') }}</p>
        <div v-if="lastParticipants.length" class="last-talents-cards">
          <div v-for="(participant, index) in lastParticipants" :key="index" class="card talent-card" @click="goToCandidateResults(participant)">
            <ProgressCircle :isNone="participant?.results?.compatibility === undefined" :size="40" :stroke="5" :gapAngle="90" :percentage="participant?.results?.compatibility * 100" />
            <div class="no-grow">
              <p class="candidate-name">
                {{ participant?.candidate?.first_name || participant?.pending_first_name || participant?.pending_email }} {{ participant?.candidate?.last_name || participant?.pending_last_name || '' }}
              </p>
              <p class="pass-date">
                {{ $t('app.campaign.summary.lastAvailableResults.assessmentPassedLabel') }} {{ useDateFormat(participant.results.completed_at, 'DD/MM/YYYY') }}
              </p>
            </div>
          </div>
        </div>
        <Empty v-else :message="$t('app.home.noResults')" :dark="isDark" icon="folder" />
      </div>
    </div>
  
    <div class="summary-charts-section">
      <div class="card talent-completion-chart">
        <div class="card-label">
          <div class="card-title" v-text="$t('app.home.completionRateTitle')" />
        </div>
        <div class="completion-stats-container">
          <Doughnut
            :data="doughnutData"
            :displayTotal="true"
            :cutoutPercent="'60%'"
            :radius="'90%'"
            :size="200"
            :totalLabel="
              $t('app.home.talentsLabel', {
                count: doughnutData.datasets[0].data.reduce((a, b) => a + b, 0),
              })
            "
          />
        </div>
      </div>
      <div class="card talent-number-chart">
        <p class="card-label">{{ $t('app.campaign.summary.monthlyParticipantCountGraph.label') }}</p>
        <div class="chart">   
          <TalentNumberChart></TalentNumberChart>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.button-header {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-bottom: 16px;

  .put-to-end {
    margin-left: auto;
  }
}

.cards-container {
  margin-bottom: 16px;
  display: flex;
  flex-direction: row;
  gap: 16px;

  .card-item {
    flex: 1 1 0px;
  }
}

.best-and-last-talents-block {
  display: flex;
  flex-direction: row;
  margin-bottom: 24px;
  width: 100%;
  gap: 16px;

  .empty-table-wrapper {
    height: 200px;
  }

  .best-talents {
    background-color: var(--white-color);
    width: 50%;
    padding: 24px;
    border: 1px solid var(--light-grey-color);
    border-radius: 12px;
  }

  .last-talents {
    background-color: var(--white-color);
    width: 50%;
    padding: 24px;
    border: 1px solid var(--light-grey-color);
    border-radius: 12px;
  }
}

.card-label {
  font-weight: 600;
  font-size: 15px;
  font-family: $bioSans;
  padding-bottom: 16px;
}

.last-talents-cards {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.talent-card {
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-items: start;
  border-radius: 4px;
  gap: 12px;
  padding: 8px 14px 8px 14px;
  width: 100%;

  &:hover {
    background-color: var(--light-white-color);
    border-color: $primary-color;
  }

  .no-grow {
    flex-shrink: 1;    /* allow shrinking */
    flex-grow: 0;
    min-width: 0;
    overflow: hidden;
  }

  .candidate-name {
    font-weight: 400;
    font-size: 14px;
    font-family: $bioSans;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .pass-date {
    font-family: $bioSans;
    font-weight: 400;
    font-size: 12px;
    color: var(--secondary-color);
  }
}

.completion-stats-container {
  width: 100%;
  display: flex;
  justify-content: center;
}

.summary-charts-section {
  display: flex;
  flex-direction: row;
  width: 100%;
  gap: 16px;

  .talent-completion-chart {
    padding: 24px;
    width: 50%;
  }

  .talent-number-chart {
    padding: 24px;
    width: 50%;
  }

  .chart {
    margin-top: 16px;
  }
}


</style>

<script setup lang="ts">
import { useAppStore } from "@/stores/app.js";
import { useDateFormat } from '@vueuse/core'
import { useDark } from "@vueuse/core";

import Doughnut from "@/components/charts/Doughnut.vue";
import TalentNumberChart from "@/components/campaigns/TalentNumberChart.vue";
import Empty from "@/components/table/Empty.vue";
import Button from "@/components/global/Button.vue";
import SlidingPanel from "@/components/sliding-panel/SlidingPanel.vue";
import AddTalentPanel from "@/components/sliding-panel/campaign/AddTalentPanel.vue";
import SimpleStatCard from "@/components/global/SimpleStatCard.vue";

import TalentNumberIcon from '@/assets/images/icon/campaign/summary/talent_number.svg?component';
import CompatibilityScoreIcon from '@/assets/images/icon/campaign/summary/compatibility_score.svg?component';
import PlusIcon from '@/assets/images/icon/campaign/plus.svg?component';

import { useParticipations } from "@/queries/participant.query";
import { useParticipationsStats } from "@/queries/participant.query";
import { useCampaign } from "@/queries/campaigns.query";

import { usePermissions } from "@/composables/permissions";

const isDark = useDark();
const appStore = useAppStore();
const { t } = useI18n();

const { data: participations, isPending: isParticipationsPending } = useParticipations();
const { data: participationStats } = useParticipationsStats();
const { data: campaign} = useCampaign();

const isPanelOpen = ref(false);

defineProps({
  search: {
    type: Object,
  },
});

const doughnutData = computed(() => {
  let data = [0, 0, 0];

  if (!isParticipationsPending.value) {
    participations.value?.forEach((participant) => {
      if (participant.state === 0) { // not started
        data[2] += 1;
      } else if (participant.state === 1) { // ongoing
        data[1] += 1;
      } else if (participant.state === 2) { // finished
        data[0] += 1;
      }
    });
  }
  return {
    labels: [t("app.home.completedEvaluations"), t("app.home.ongoingEvaluations"), t("app.home.notStartedEvaluations")],
    datasets: [
      {
        backgroundColor: ["#F2CB36", "#D2D0D0", isDark.value ? "#000000" : "#1D1D1D"],
        data: data,
      },
    ],
  };
});

const lastParticipants = computed(() => {
  if (isParticipationsPending.value) return [];
  return participations.value.filter((p) => p.state === 2 && p?.results).sort((participantA, participantB) => {
    const dA = Date.parse(participantA.results.completed_at);
    const dB = Date.parse(participantB.results.completed_at);

    if (dA < dB) {
      return 1;
    } else if (dA > dB) {
      return -1;
    }
    return 0;
  }).slice(0, 3);
});

const bestParticipants = computed(() => {
  if (isParticipationsPending.value) return [];
  return participations.value.filter((p) => p.state === 2 && p?.results).sort((participantA, participantB) => {
    const compatibilityA = participantA.results.compatibility;
    const compatibilityB = participantB.results.compatibility;

    if (compatibilityA < compatibilityB) {
      return 1;
    } else if (compatibilityA > compatibilityB) {
      return -1;
    }
    return 0;
  }).slice(0, 3);
});

function goToCandidateResults(participant) {
  appStore.changeTab('Results');
  setTimeout(() => {
    appStore.openedCandidateResults = [participant];
    appStore.talentResultPanelIsOpen = true;
  }, 100);
}

function goToNewTalent() {
  isPanelOpen.value = true;
}

</script>
